import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useAssessment } from '../../hooks/useAssessment';
import { EU_AI_ACT_OBLIGATIONS } from '../../utils/euAiAct';
import { RemediationAction, RemediationStatus } from '../../types/assessment';
import { CheckCircle, Clock, AlertCircle, Calendar, User, ChevronRight, TrendingUp, Filter } from 'lucide-react';
export function RemediationActions() {
  const navigate = useNavigate();
  const {
    assessment,
    updateRemediationAction
  } = useAssessment();
  const [filterStatus, setFilterStatus] = useState<'all' | RemediationStatus>('all');
  const [sortBy, setSortBy] = useState<'dueDate' | 'status' | 'created'>('status');
  const remediationActions = assessment.remediationActions || [];
  // Filter actions
  const filteredActions = remediationActions.filter(action => {
    if (filterStatus === 'all') return true;
    return action.status === filterStatus;
  });
  // Sort actions
  const sortedActions = [...filteredActions].sort((a, b) => {
    if (sortBy === 'dueDate') {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    }
    if (sortBy === 'status') {
      const statusOrder = {
        open: 0,
        'in-progress': 1,
        completed: 2
      };
      return statusOrder[a.status] - statusOrder[b.status];
    }
    // sortBy === 'created'
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  const handleUpdateStatus = (actionId: string, status: RemediationStatus) => {
    updateRemediationAction(actionId, status);
  };
  const getObligationTitle = (obligationId: string): string => {
    const obligation = EU_AI_ACT_OBLIGATIONS.find(o => o.id === obligationId);
    return obligation ? obligation.title : obligationId;
  };
  const getStatusIcon = (status: RemediationStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-teal-500" data-id="element-550" />;
      case 'in-progress':
        return <Clock className="h-5 w-5 text-amber-500" data-id="element-551" />;
      case 'open':
        return <AlertCircle className="h-5 w-5 text-slate-500" data-id="element-552" />;
    }
  };
  const getStatusBadgeVariant = (status: RemediationStatus): 'teal' | 'amber' | 'slate' => {
    switch (status) {
      case 'completed':
        return 'teal';
      case 'in-progress':
        return 'amber';
      case 'open':
        return 'slate';
    }
  };
  const isOverdue = (action: RemediationAction): boolean => {
    if (!action.dueDate || action.status === 'completed') return false;
    return new Date(action.dueDate) < new Date();
  };
  // Calculate statistics
  const openCount = remediationActions.filter(a => a.status === 'open').length;
  const inProgressCount = remediationActions.filter(a => a.status === 'in-progress').length;
  const completedCount = remediationActions.filter(a => a.status === 'completed').length;
  const overdueCount = remediationActions.filter(a => isOverdue(a)).length;
  return <div className="max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-553">
      {/* Header */}
      <div className="mb-8" data-id="element-554">
        <div className="flex items-center justify-between mb-4" data-id="element-555">
          <div data-id="element-556">
            <h1 className="text-3xl font-bold mb-3" data-id="element-557">Remediation Actions</h1>
            <p className="text-slate-400 text-lg" data-id="element-558">
              Track and manage compliance remediation activities across all
              obligations.
            </p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/assessment/analysis')} data-id="element-559">
            Back to Analysis
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-id="element-560">
        <Card className="border-slate-700" data-id="element-561">
          <div className="flex items-center justify-between" data-id="element-562">
            <div data-id="element-563">
              <div className="text-2xl font-bold text-slate-300" data-id="element-564">
                {openCount}
              </div>
              <div className="text-xs text-slate-400" data-id="element-565">Open</div>
            </div>
            <AlertCircle className="h-8 w-8 text-slate-500" data-id="element-566" />
          </div>
        </Card>

        <Card className="border-amber-500/20" data-id="element-567">
          <div className="flex items-center justify-between" data-id="element-568">
            <div data-id="element-569">
              <div className="text-2xl font-bold text-amber-400" data-id="element-570">
                {inProgressCount}
              </div>
              <div className="text-xs text-slate-400" data-id="element-571">In Progress</div>
            </div>
            <Clock className="h-8 w-8 text-amber-500" data-id="element-572" />
          </div>
        </Card>

        <Card className="border-teal-500/20" data-id="element-573">
          <div className="flex items-center justify-between" data-id="element-574">
            <div data-id="element-575">
              <div className="text-2xl font-bold text-teal-400" data-id="element-576">
                {completedCount}
              </div>
              <div className="text-xs text-slate-400" data-id="element-577">Completed</div>
            </div>
            <CheckCircle className="h-8 w-8 text-teal-500" data-id="element-578" />
          </div>
        </Card>

        <Card className={`${overdueCount > 0 ? 'border-red-500/20 bg-red-500/5' : 'border-slate-700'}`} data-id="element-579">
          <div className="flex items-center justify-between" data-id="element-580">
            <div data-id="element-581">
              <div className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-400' : 'text-slate-300'}`} data-id="element-582">
                {overdueCount}
              </div>
              <div className="text-xs text-slate-400" data-id="element-583">Overdue</div>
            </div>
            <Calendar className={`h-8 w-8 ${overdueCount > 0 ? 'text-red-500' : 'text-slate-500'}`} data-id="element-584" />
          </div>
        </Card>
      </div>

      {/* Filters and Sort */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-585">
        <div className="flex items-center space-x-2" data-id="element-586">
          <Filter className="h-4 w-4 text-slate-500" data-id="element-587" />
          <span className="text-sm text-slate-400" data-id="element-588">Filter:</span>
          <button onClick={() => setFilterStatus('all')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filterStatus === 'all' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-navy-800 text-slate-400 border border-slate-700 hover:border-slate-600'}`} data-id="element-589">
            All
          </button>
          <button onClick={() => setFilterStatus('open')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filterStatus === 'open' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-navy-800 text-slate-400 border border-slate-700 hover:border-slate-600'}`} data-id="element-590">
            Open
          </button>
          <button onClick={() => setFilterStatus('in-progress')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filterStatus === 'in-progress' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-navy-800 text-slate-400 border border-slate-700 hover:border-slate-600'}`} data-id="element-591">
            In Progress
          </button>
          <button onClick={() => setFilterStatus('completed')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filterStatus === 'completed' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-navy-800 text-slate-400 border border-slate-700 hover:border-slate-600'}`} data-id="element-592">
            Completed
          </button>
        </div>

        <div className="flex items-center space-x-2" data-id="element-593">
          <span className="text-sm text-slate-400" data-id="element-594">Sort by:</span>
          <select value={sortBy} onChange={e => setSortBy(e.target.value as 'dueDate' | 'status' | 'created')} className="bg-navy-800 border border-slate-700 rounded-md px-3 py-1.5 text-sm text-slate-300" data-id="element-595">
            <option value="status" data-id="element-596">Status</option>
            <option value="dueDate" data-id="element-597">Due Date</option>
            <option value="created" data-id="element-598">Created Date</option>
          </select>
        </div>
      </div>

      {/* Actions List */}
      {sortedActions.length === 0 ? <Card className="text-center py-16 border-dashed" data-id="element-599">
          <TrendingUp className="h-16 w-16 text-slate-600 mx-auto mb-4" data-id="element-600" />
          <h3 className="text-xl font-semibold text-white mb-2" data-id="element-601">
            {filterStatus === 'all' ? 'No remediation actions yet' : `No ${filterStatus} actions`}
          </h3>
          <p className="text-slate-400 mb-6" data-id="element-602">
            {filterStatus === 'all' ? 'Create remediation actions from the gap analysis to track compliance improvements.' : 'Try adjusting your filters to see more actions.'}
          </p>
          <Button onClick={() => navigate('/assessment/analysis')} data-id="element-603">
            Go to Gap Analysis
          </Button>
        </Card> : <div className="space-y-3" data-id="element-604">
          {sortedActions.map(action => {
        const overdue = isOverdue(action);
        return <Card key={action.id} className={`transition-all hover:border-teal-500/50 ${overdue ? 'border-red-500/30 bg-red-500/5' : ''}`} data-id="element-605">
                <div className="flex items-start justify-between gap-4" data-id="element-606">
                  <div className="flex items-start space-x-4 flex-1" data-id="element-607">
                    {/* Status Icon */}
                    <div className="mt-1" data-id="element-608">{getStatusIcon(action.status)}</div>

                    {/* Content */}
                    <div className="flex-1 min-w-0" data-id="element-609">
                      {/* Header */}
                      <div className="flex items-center space-x-2 mb-2" data-id="element-610">
                        <Badge variant={getStatusBadgeVariant(action.status)} className="text-xs" data-id="element-611">
                          {action.status === 'in-progress' ? 'In Progress' : action.status.charAt(0).toUpperCase() + action.status.slice(1)}
                        </Badge>
                        {overdue && <Badge variant="red" className="text-xs" data-id="element-612">
                            Overdue
                          </Badge>}
                      </div>

                      {/* Description */}
                      <h3 className="text-base font-semibold text-white mb-2" data-id="element-613">
                        {action.description}
                      </h3>

                      {/* Obligation */}
                      <div className="flex items-center text-sm text-slate-400 mb-2" data-id="element-614">
                        <span className="font-mono text-xs text-slate-500 mr-2" data-id="element-615">
                          {action.obligationId}
                        </span>
                        <ChevronRight className="h-3 w-3 mx-1" data-id="element-616" />
                        <span data-id="element-617">{getObligationTitle(action.obligationId)}</span>
                      </div>

                      {/* Metadata */}
                      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400" data-id="element-618">
                        <span className="flex items-center" data-id="element-619">
                          <User className="h-3 w-3 mr-1" data-id="element-620" />
                          {action.owner}
                        </span>
                        {action.dueDate && <span className={`flex items-center ${overdue ? 'text-red-400 font-medium' : ''}`} data-id="element-621">
                            <Calendar className="h-3 w-3 mr-1" data-id="element-622" />
                            Due {new Date(action.dueDate).toLocaleDateString()}
                          </span>}
                        <span className="text-slate-500" data-id="element-623">
                          Created{' '}
                          {new Date(action.createdAt).toLocaleDateString()}
                        </span>
                      </div>

                      {/* Notes */}
                      {action.notes && <div className="mt-3 p-3 bg-navy-900 rounded border border-slate-800" data-id="element-624">
                          <p className="text-sm text-slate-300" data-id="element-625">
                            {action.notes}
                          </p>
                        </div>}
                    </div>
                  </div>

                  {/* Status Selector */}
                  <div className="flex-shrink-0" data-id="element-626">
                    <select value={action.status} onChange={e => handleUpdateStatus(action.id, e.target.value as RemediationStatus)} className="bg-navy-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-300 hover:border-slate-600 transition-colors" data-id="element-627">
                      <option value="open" data-id="element-628">Open</option>
                      <option value="in-progress" data-id="element-629">In Progress</option>
                      <option value="completed" data-id="element-630">Completed</option>
                    </select>
                  </div>
                </div>
              </Card>;
      })}
        </div>}
    </div>;
}